import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FC,
  type JSX,
} from 'react';

import {
  Button,
  Icon,
  MiniModal,
  Typography,
  getStoreValue,
  toast,
  setStoreValue,
  Tooltip,
  Toggle,
} from 'pixel-react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';

import AI_GIF from '@/assets/ai_sparkle.gif';
import {
  BEFORE_RUNNING_STATUSES,
  UPGRADE_FORM_SUBMITTED_MESSAGE,
} from '@/components/ExecutionDetails/constants';
import ExecutionDetails from '@/components/ExecutionDetails/ExecutionDetailsDrawer';
import DownloadClient from '@/pages/Configuration/environmentConfigurations/components/DownloadClient/DownloadClient';
import { UpgradeDrawer } from '@/pages/Configuration/Upgrade/UpgradeDrawer';
import { RootState } from '@/store';
import type { Test_Optimize_ResponseObject } from '@/store/commonApiType/testOptimizeUserType';
import { useLazyGetExecutionDetailsQuery } from '@/store/executionDetailsDrawerApi';
import { ExecutionResponseObjType } from '@/store/executionDetailsDrawerApi/types';
import { useAppSelector } from '@/store/hooks';
import { useSelectedServiceQuery } from '@/store/integrations/ServiceProviderSlice';
import {
  useGetAllResultConfigQuery,
  useUpdateTokenCountMutation,
} from '@/store/privilegeAI/privilegeai';

import { AUTONOMOUS_SUBBAR_SLOT } from './constants';
import styles from './NotificationHeader.module.scss';
import type {
  ExecutionRowDataType,
  NotificationHeaderProps,
  TokenData,
} from './types';

export const NotificationHeader: FC<NotificationHeaderProps> = ({
  iconsList,
  handleUpgrade,
  isUpgradeDrawerOpen,
  closeUpgradeDrawer,
  handleIconClick,
  downloadClientRef,
  isDownloadModalOpen,
  closeDownloadModal,
}): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const [runCount, setRunCount] = useState(0);
  const [parallelRuns, setParallelRuns] = useState(0);
  const [showExecutionDetailDrawer, setShowExecutionDetailDrawer] =
    useState<boolean>(false);
  const ignoreRef = useRef<HTMLDivElement>(null);
  const [fetchExecutionDetails] = useLazyGetExecutionDetailsQuery();
  const [executionDetails, setExecutionDetails] = useState<
    ExecutionRowDataType[]
  >([]);
  const [scriptType, setScriptType] = useState<string[]>([]);
  const [terminated, setTerminated] = useState<string[]>([]);
  const [checked, setChecked] = useState<boolean>(false);
  const [licenseFeatures, setLicenseFeatures] = useState<string[]>([]);
  const currentProject = getStoreValue('current-project') as {
    id: string;
    type: string;
  };
  const { data: aiPrivilegeData } = useGetAllResultConfigQuery(undefined, {
    skip: currentProject?.id === 'All Projects',
  });
  const aiToggleDisplay = Boolean(
    aiPrivilegeData?.responseObject?.aiLicenseLevelPrivileges?.aiEnabled,
  );
  const { data: selectedServiceData } = useSelectedServiceQuery(
    currentProject?.id ?? '',
    { skip: !currentProject?.id },
  );
  const defaultSelectedServiceProvider =
    selectedServiceData?.responseObject?.serviceProvider ?? '';
  const [availableAiTokens, setAvailableAiTokens] = useState<TokenData>();
  const [countTotalTokens] = useUpdateTokenCountMutation();
  const pathname = location?.pathname || '';
  const { privilegeDetails } = useAppSelector(
    (state: RootState) => state.privilege,
  );
  const userDetails = getStoreValue(
    'test-optimize-user',
  ) as Test_Optimize_ResponseObject;

  const profileData = useSelector(
    (state: RootState) => state.profileDataSlice.profileData,
  );
  const {
    expires_in: expiresIn,
    licenseType,
    licenseStatus,
    privilege,
  } = userDetails?.responseObject ?? {};

  const fetchData = async () => {
    try {
      const response = await fetchExecutionDetails();
      if (response?.data?.responseCode === 200) {
        const executionDetailResponse = response?.data?.responseObject;
        const parallelRunCounts = response?.data?.responseMap;
        setRunCount(parallelRunCounts?.parallelRunsOccupied);
        setParallelRuns(parallelRunCounts?.numberOfParallelRuns);
        const executionTableData: ExecutionRowDataType[] =
          executionDetailResponse?.map((data: ExecutionResponseObjType) => {
            data?.type.toLowerCase() === 'suite'
              ? setScriptType(prev => [...prev, data?.name])
              : '';
            ['Running', ...BEFORE_RUNNING_STATUSES].includes(
              data?.executionStatus,
            )
              ? setTerminated(prev => [...prev, data?._id])
              : '';
            return {
              executionId: data?.executionName || data?._id,
              executionName: data?.name ? data.name : 'N/A',
              project: data?.projectName,
              machineName:
                data?.preExecution?.selectedSystem
                  ?.map(
                    system =>
                      system?.machineInstances
                        ?.map(
                          (instance: { machineInfo: { hostName: string } }) =>
                            instance?.machineInfo?.hostName,
                        )
                        .join(', ') || 'MachineName Not Available',
                  )
                  .join(',') || 'MachineName Not Available',
              initiatedBy: data?.createdByUname,
              status: data?.executionStatus,
              type: data?.type,
              executionType: data?.executionType,
            };
          });
        setExecutionDetails(executionTableData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchExecutionDetails]);
  const handleClick = async () => {
    fetchData();
    setShowExecutionDetailDrawer(true);
  };
  const closeExecutionDetailsDrawer = () => {
    setShowExecutionDetailDrawer(false);
  };
  useEffect(() => {
    const pathSegments = location.pathname.split('/');
    const isAiMode = pathSegments[3] === 'aiGeneration';
    setChecked(isAiMode);
    if (isAiMode) {
      setStoreValue('ai-mode', 'true');
    } else {
      setStoreValue('ai-mode', 'false');
    }
  }, [location.pathname]);
  const handleToggleChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const toggledToAi = (e.target as HTMLInputElement).checked;
    const newChecked = !checked;
    setChecked(newChecked);
    if (newChecked) {
      navigate('/app/test-dev/aiGeneration');
      setStoreValue('activeFlow', 'aiLanding');
    } else {
      navigate('/app/test-dev/scripts');
    }
  };
  const getExpiresInText = (expiresIn: number) => {
    if (expiresIn === 0) {
      return 'today';
    } else if (expiresIn === 1) {
      return `in ${expiresIn} day`;
    } else {
      return `in ${expiresIn} days`;
    }
  };
  const handleUpgradeForm = useCallback(
    () =>
      profileData.message === UPGRADE_FORM_SUBMITTED_MESSAGE
        ? toast.info(profileData.message)
        : handleUpgrade(),
    [profileData.message],
  );
  const getTotalTokens = async () => {
    try {
      const response = await countTotalTokens({
        totalTokens: 0,
        projectid: currentProject?.id,
      });
      setAvailableAiTokens(response?.data?.responseObject);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    setStoreValue(
      'currentSelectedServiceProvider',
      defaultSelectedServiceProvider,
    );
    if (defaultSelectedServiceProvider === 'DefaultFireFlink') {
      void getTotalTokens();
    }
  }, [defaultSelectedServiceProvider, currentProject?.id]);

  useEffect(() => {
    if (privilegeDetails) {
      setLicenseFeatures(privilegeDetails?.licenseFeatures ?? []);
    }
  }, [privilegeDetails]);
  const showNotifications = licenseFeatures.some(
    feature => feature !== 'ManualTestcases' && feature !== 'Defects',
  );
  const showUpgradeButton =
    Number(expiresIn) >= 0 &&
    licenseStatus === 'ACTIVE' &&
    licenseType === 'C-Basic' &&
    privilege === 'Super Admin';
  return (
    // data-at-subbar (AUTONOMOUS_SUBBAR_ATTR): Autonomous Testing side panels open below this row
    <div className={styles.notification_header_container} data-at-subbar="">
      <div className={styles.header}>
        <div className={styles.ai_container}>
          {currentProject?.id !== 'All Projects' && aiToggleDisplay && (
            <>
              <div className={styles.ai_toggle}>
                <Typography
                  className={styles.fireflink_ai}
                  fontSize={16}
                  fontWeight="bold"
                >
                  FireFlink AI
                </Typography>
                <img
                  src={AI_GIF}
                  alt="sparkle gif"
                  height={16}
                  width={16}
                  className={styles.ai_gif}
                />
                <Toggle
                  size="medium"
                  id="switchToAiMode"
                  checked={checked}
                  onChange={handleToggleChange}
                />
              </div>
              {defaultSelectedServiceProvider === 'DefaultFireFlink' && (
                <>
                  <div className={styles.vertical_divider} />
                  <div
                    onMouseEnter={getTotalTokens}
                    className={styles.token_wrapper}
                  >
                    <Tooltip
                      title={`Remaining Tokens ${Math.max(0, availableAiTokens?.remainingToken ?? 0)}`}
                    >
                      <Icon name="ai_token" />
                      <Typography fontWeight="semi-bold">{`${
                        (availableAiTokens?.remainingToken ?? 0) < 0
                          ? 0
                          : (availableAiTokens?.formattedTokenCount ?? 0)
                      } Tokens`}</Typography>
                    </Tooltip>
                  </div>
                </>
              )}
            </>
          )}
          {/* Autonomous Testing portals its running-agent indicator here, on the left; the slot itself has no box */}
          {pathname.startsWith('/app/autonomous-testing') && (
            <div id={AUTONOMOUS_SUBBAR_SLOT} className={styles.slot} />
          )}
        </div>
        {showNotifications && (
          <>
            <div className={styles.upgrade_container}>
              {showUpgradeButton && (
                <>
                  <div className={styles.upgrade_text}>
                    <Typography
                      fontWeight="semi-bold"
                      color="var(--description-text)"
                    >
                      Your Free Trial License Will Expire{' '}
                      {getExpiresInText(Number(expiresIn))}
                    </Typography>
                  </div>
                  <Button
                    label="Upgrade"
                    variant="custom"
                    size="medium"
                    buttonHeight="28px"
                    onClick={handleUpgradeForm}
                    backgroundColor="var(--ff-upgrade-button-bg-color)"
                    className={styles.upgrade_button_text_color}
                  />
                </>
              )}
            </div>
            {isUpgradeDrawerOpen && (
              <UpgradeDrawer closeUpgradeDrawer={closeUpgradeDrawer} />
            )}
            <div className={styles.notification_container}>
              <div className={styles.notification_icons}>
                {iconsList.map((icons, index) => (
                  <div key={index} className={styles.icon}>
                    <Tooltip title={icons?.tooltip}>
                      <Icon
                        width={16}
                        height={16}
                        name={icons?.icon}
                        onClick={() => handleIconClick(icons?.icon)}
                        ref={downloadClientRef}
                        hoverEffect
                      />
                    </Tooltip>
                    {isDownloadModalOpen && (
                      <MiniModal
                        anchorRef={downloadClientRef}
                        modalProperties={{
                          width: 404,
                          zIndex: 4100,
                          borderRadius: 8,
                          padding: 0,
                        }}
                        headerProps={<></>}
                        childContent={
                          <DownloadClient
                            closeDownloadModal={closeDownloadModal}
                            ignoreRef={ignoreRef}
                          />
                        }
                        footerContent={<></>}
                        isWrapped
                        extraTopSpace={{ normalModal: 15 }}
                        anchorLeftDistanceForWrapper={180}
                        anchorRefLeftNum={110}
                        firstAnchorRef={downloadClientRef}
                        outSideClick={closeDownloadModal}
                        ignoreRefs={[ignoreRef]}
                      />
                    )}
                  </div>
                ))}
              </div>
              <div className={styles.parallel_run}>
                <Tooltip
                  title={`${
                    runCount < parallelRuns
                      ? `You have ${runCount} executions in use out of ${parallelRuns} parallel runs in your license.`
                      : `You have ${runCount} executions in use out of ${parallelRuns} parallel runs in your license. For more runs, kindly upgrade your license.`
                  }`}
                >
                  <div className={styles.parallel_run_icon}>
                    <Icon
                      name="refresh_icon"
                      onClick={fetchData}
                      color={`${runCount >= parallelRuns ? 'var(--error)' : 'var(--brand-color)'}`}
                      cursorType="pointer"
                    />
                  </div>
                  <div
                    className={styles.parallel_run_button_vertical_line}
                  ></div>
                  <Typography
                    as="div"
                    onClick={handleClick}
                    color={`${runCount >= parallelRuns ? 'var(--error)' : 'var(--brand-color)'}`}
                  >{`${runCount} running of ${parallelRuns} parallel runs`}</Typography>
                </Tooltip>
              </div>
            </div>
          </>
        )}
      </div>

      {showExecutionDetailDrawer && (
        <ExecutionDetails
          onClose={closeExecutionDetailsDrawer}
          rowData={executionDetails}
          scriptType={scriptType}
          terminated={terminated}
          onGoingFetchData={fetchData}
        />
      )}
    </div>
  );
};
